import React, { useMemo, useState } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { Button, Dialog, cn } from '../../ui';
import { getIcon, ICON_CATALOG } from '../icons';

/**
 * IconPicker
 *
 * Sustituye al <select> nativo del icono en TipoForm. Muestra un
 * botón compacto con el icono actual + su nombre y, al pulsar, abre
 * un Dialog con un grid agrupado por familias (`ICON_CATALOG` en
 * icons.js) y un buscador por nombre. Seleccionar un icono cierra el
 * picker y dispara onChange.
 *
 * props:
 *   value     string en kebab-case (mismo formato que guarda BBDD,
 *             ej. 'laptop', 'shopping-bag'). '' / null si no hay.
 *   onChange  (name) => void
 *   disabled  bool
 */
export default function IconPicker({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const Current = getIcon(value);

  // Filtro: cuando el usuario escribe, las categorías sin matches se
  // ocultan; las que sí tienen matches conservan solo los iconos que
  // contienen el query. Filtrado por nombre (kebab-case) en minúsculas.
  const visibleCatalog = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ICON_CATALOG;
    return ICON_CATALOG.map((g) => ({
      ...g,
      icons: g.icons.filter((n) => n.toLowerCase().includes(q)),
    })).filter((g) => g.icons.length > 0);
  }, [query]);

  const hasResults = visibleCatalog.length > 0;

  function pick(name) {
    onChange(name);
    setOpen(false);
    setQuery('');
  }

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        className={cn(
          'w-full border border-slate-200 rounded-md px-3 py-2 bg-white',
          'flex items-center gap-2 text-left',
          'focus:outline-none focus:ring-2 focus:ring-blue-200',
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-slate-300'
        )}
      >
        <span className="p-1.5 rounded-md bg-slate-100 shrink-0">
          <Current className="w-4 h-4 text-[hsl(var(--brand-light))]" />
        </span>
        <span className="flex-1 text-sm text-slate-700 truncate font-mono">
          {value || 'Selecciona un icono'}
        </span>
        <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Selecciona un icono"
        description={`${ICON_CATALOG.reduce((n, g) => n + g.icons.length, 0)} iconos disponibles, agrupados por familia.`}
        footer={
          <div className="flex items-center justify-end">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre (car, mail, shield…)"
              className="w-full border border-slate-200 rounded-md pl-8 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="max-h-[55vh] overflow-y-auto pr-1 -mr-1">
            {!hasResults && (
              <p className="text-sm text-slate-400 text-center py-8">
                Sin resultados para "{query}".
              </p>
            )}
            {visibleCatalog.map((grupo) => (
              <div key={grupo.category} className="mb-4 last:mb-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  {grupo.category}
                </p>
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
                  {grupo.icons.map((name) => {
                    const Icon = getIcon(name);
                    const selected = name === value;
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => pick(name)}
                        title={name}
                        className={cn(
                          'aspect-square flex items-center justify-center rounded-md border transition-colors',
                          selected
                            ? 'bg-[hsl(var(--brand-light))]/10 border-[hsl(var(--brand-light))] text-[hsl(var(--brand-light))]'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400 hover:bg-slate-50'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Dialog>
    </>
  );
}
